/**
 * invoiceCalc.js — Core invoice amount calculations.
 *
 * Single source of truth for computing derived invoice amounts on the
 * frontend. Mirrors the logic in the backend's invoice PDF helpers.
 *
 * Exports:
 *   calcInvoiceAmount(inv)  — signed, directional amount used for TABLE DISPLAY.
 *   calcGrossBilled(inv)    — always non-negative "gross billed volume" for DASHBOARD.
 *
 * Direct Allocation entries (is_direct_allocation: true):
 *   When any non-sale entry has is_direct_allocation=true, the calculation splits
 *   into a "shared" pool (split by ownership %) and a "direct" pool (100% to this
 *   owner, no ownership split).  When NO direct allocations exist the output is
 *   bit-for-bit identical to the original single-pool logic — zero regression risk.
 */

// ─── helpers ─────────────────────────────────────────────────────────────────

// BUG-09 fix: guard against undefined price/units so we never produce NaN.
// Use `?? 0` / `|| 1` so a missing field defaults to zero rather than NaN.
const entryTotal = (e) => e.total ?? ((e.price || 0) * (e.units || 1));

// ─── calcInvoiceAmount ────────────────────────────────────────────────────────

/**
 * @param {Object} inv  Invoice object from the API (entries array must be present)
 * @returns {{
 *   expenseSubtotal, proceedsSubtotal, discountTotal, expenseTotal,
 *   saleProceeds, hasSaleDetails, hasOtherProceeds,
 *   grandTotal, amount, isPayable,
 *   hasDirectAllocations,              — true when any entry has is_direct_allocation
 *   sharedExpenseSubtotal,             — shared expense entries pre-discount
 *   sharedProceedsSubtotal,            — shared proceeds entries
 *   directSubtotal,                    — net of direct entries after proportional discount
 *   directExpenseSubtotal,             — direct expense entries pre-discount
 *   directProceedsSubtotal,            — direct proceeds entries
 *   discountOnShared, discountOnDirect — proportional split of invoice discount
 *   sharedGrandTotal,                  — shared pool before ownership split (= "Shared Total")
 * }}
 */
export function calcInvoiceAmount(inv) {
  const entries = inv.entries || [];

  // ── Detect Direct Allocations ───────────────────────────────────────────────
  const hasDirectAllocations = entries.some(
    (e) => !e.sale_opportunity_id && e.is_direct_allocation
  );

  // ── SHARED-ONLY PATH (no direct allocations) — identical to original ────────
  if (!hasDirectAllocations) {
    const expenseEntries  = entries.filter((e) => !e.sale_opportunity_id && !e.is_proceeds);
    const proceedsEntries = entries.filter((e) => !e.sale_opportunity_id && !!e.is_proceeds);

    const expenseSubtotal  = expenseEntries.reduce((s, e) => s + entryTotal(e), 0);
    const proceedsSubtotal = proceedsEntries.reduce((s, e) => s + entryTotal(e), 0);
    const discountTotal    = inv.discount_total || 0;
    const expenseTotal     = expenseSubtotal - discountTotal;
    const hasOtherProceeds = proceedsSubtotal > 0;

    const saleNetProceeds = inv.sale_net_proceeds || 0;
    const commission      = inv.commission_amount || 0;
    const hasSaleDetails  = !!(inv.add_sale_proceeds);
    const saleProceeds    = hasSaleDetails ? saleNetProceeds - commission : 0;
    const isRna           = !!(inv.add_sale_proceeds && inv.sale_result === 'rna');

    let grandTotal;
    if (hasSaleDetails) {
      const totalProceeds = isRna ? 0 : saleProceeds + proceedsSubtotal;
      grandTotal = isRna
        ? Math.abs(saleProceeds) + expenseTotal
        : totalProceeds - expenseTotal;
    } else if (hasOtherProceeds) {
      grandTotal = proceedsSubtotal - expenseTotal;
    } else {
      grandTotal = expenseTotal;
    }

    // BUG-11 fix: explicit null-check so owner_percent=0 gives $0, not 100%.
    const amount = ((inv.owner_percent != null ? inv.owner_percent : 100) / 100) * grandTotal;

    const isPayable = (
      (!isRna && hasSaleDetails && (saleProceeds + proceedsSubtotal) > expenseTotal) ||
      (hasOtherProceeds && !hasSaleDetails && proceedsSubtotal > expenseTotal)         ||
      !!inv.is_commission_split_invoice
    );

    return {
      expenseSubtotal, proceedsSubtotal, discountTotal, expenseTotal,
      saleProceeds, hasSaleDetails, hasOtherProceeds,
      grandTotal, amount, isPayable,
      // Direct Allocation fields — all zero/false for the no-DA path
      hasDirectAllocations: false,
      sharedExpenseSubtotal: expenseSubtotal,
      sharedProceedsSubtotal: proceedsSubtotal,
      directSubtotal: 0,
      directExpenseSubtotal: 0,
      directProceedsSubtotal: 0,
      discountOnShared: discountTotal,
      discountOnDirect: 0,
      sharedGrandTotal: grandTotal,
    };
  }

  // ── DIRECT ALLOCATION PATH ──────────────────────────────────────────────────
  // Split entries into shared vs direct, and expense vs proceeds.
  const sharedExpenseEntries  = entries.filter((e) => !e.sale_opportunity_id && !e.is_proceeds && !e.is_direct_allocation);
  const sharedProceedsEntries = entries.filter((e) => !e.sale_opportunity_id && !!e.is_proceeds && !e.is_direct_allocation);
  const directExpenseEntries  = entries.filter((e) => !e.sale_opportunity_id && !e.is_proceeds && !!e.is_direct_allocation);
  const directProceedsEntries = entries.filter((e) => !e.sale_opportunity_id && !!e.is_proceeds && !!e.is_direct_allocation);

  const sharedExpenseSubtotal  = sharedExpenseEntries.reduce((s, e)  => s + entryTotal(e), 0);
  const sharedProceedsSubtotal = sharedProceedsEntries.reduce((s, e) => s + entryTotal(e), 0);
  const directExpenseSubtotal  = directExpenseEntries.reduce((s, e)  => s + entryTotal(e), 0);
  const directProceedsSubtotal = directProceedsEntries.reduce((s, e) => s + entryTotal(e), 0);

  // Proportional discount: splits across shared and direct expense portions.
  // BUG-07 fix: derive discountOnDirect as (total − shared) so the two always
  // sum to exactly discountTotal — independent multiplication leaves a sub-cent residual.
  const discountTotal   = inv.discount_total || 0;
  const totalExpenses   = sharedExpenseSubtotal + directExpenseSubtotal; // denominator for split
  const discountOnShared = totalExpenses > 0
    ? discountTotal * (sharedExpenseSubtotal / totalExpenses)
    : 0;
  const discountOnDirect = discountTotal - discountOnShared; // guaranteed exact sum

  const sharedExpenseNet = sharedExpenseSubtotal - discountOnShared;
  const directExpenseNet = directExpenseSubtotal - discountOnDirect;

  // Sale proceeds (always shared — "nothing about sale proceeds changes").
  const saleNetProceeds = inv.sale_net_proceeds || 0;
  const commission      = inv.commission_amount || 0;
  const hasSaleDetails  = !!(inv.add_sale_proceeds);
  const isRna           = !!(inv.add_sale_proceeds && inv.sale_result === 'rna');
  const saleProceeds    = hasSaleDetails ? saleNetProceeds - commission : 0;

  // Shared Grand Total (before ownership split) — includes sale proceeds.
  const hasOtherProceeds = (sharedProceedsSubtotal + directProceedsSubtotal) > 0;
  let sharedGrandTotal;
  if (hasSaleDetails) {
    const totalProceeds = isRna ? 0 : saleProceeds + sharedProceedsSubtotal;
    sharedGrandTotal = isRna
      ? Math.abs(saleProceeds) + sharedExpenseNet
      : totalProceeds - sharedExpenseNet;
  } else if (sharedProceedsSubtotal > 0) {
    sharedGrandTotal = sharedProceedsSubtotal - sharedExpenseNet;
  } else {
    sharedGrandTotal = sharedExpenseNet;
  }

  // ── Determine if the shared side is payable ─────────────────────────────────
  // This must be computed BEFORE the DA contribution so we know which sign to use.
  const sharedIsPayable = (
    (!isRna && hasSaleDetails && (saleProceeds + sharedProceedsSubtotal) > sharedExpenseNet) ||
    (sharedProceedsSubtotal > 0 && !hasSaleDetails && sharedProceedsSubtotal > sharedExpenseNet) ||
    !!inv.is_commission_split_invoice
  );

  // ── DA contribution — sign must mirror the shared-side convention ─────────────
  //   Payable context  (owner RECEIVES):  proceeds ADD (+), expenses REDUCE (−)
  //   Expense context  (owner OWES):      expenses ADD (+), proceeds REDUCE (−)
  const directContribution = sharedIsPayable
    ? (directProceedsSubtotal - directExpenseNet)   // payable: proceeds add
    : (directExpenseNet - directProceedsSubtotal);  // expense: expenses add

  // BUG-11 fix: use explicit null-check so owner_percent=0 is honoured as 0%,
  // not silently promoted to 100% by the `|| 100` falsy-fallback.
  const ownerPct = (inv.owner_percent != null ? inv.owner_percent : 100) / 100;
  const amount   = ownerPct * sharedGrandTotal + directContribution;

  // isPayable: re-derive from the signed amount for the DA-proceeds-only edge
  // case where the shared side has no proceeds but DA proceeds make amount > 0.
  // BUG-10 fix: a positive amount means payable regardless of sharedIsPayable.
  const isPayable = sharedIsPayable || amount > 0;

  // Backward-compat aggregate fields (for consumers that don't know about DA).
  const expenseSubtotal  = sharedExpenseSubtotal + directExpenseSubtotal;
  const proceedsSubtotal = sharedProceedsSubtotal + directProceedsSubtotal;
  const expenseTotal     = expenseSubtotal - discountTotal;

  return {
    // Existing fields preserved for backward compat
    expenseSubtotal, proceedsSubtotal, discountTotal, expenseTotal,
    saleProceeds, hasSaleDetails, hasOtherProceeds,
    grandTotal: sharedGrandTotal, // "Shared Total" in DA context
    amount, isPayable,
    // New Direct Allocation fields
    hasDirectAllocations: true,
    sharedExpenseSubtotal,
    sharedProceedsSubtotal,
    directSubtotal: directContribution,  // sign-correct for display (+ = adds, − = reduces)
    directExpenseSubtotal,
    directProceedsSubtotal,
    discountOnShared,
    discountOnDirect,
    sharedGrandTotal,
  };
}

// ─── calcGrossBilled ──────────────────────────────────────────────────────────

/**
 * Gross Billed Volume for dashboard metric boxes.
 * With Direct Allocations: shared expenses × ownership + direct expenses (full).
 * Without: identical to original.
 */
export function calcGrossBilled(inv) {
  const entries = inv.entries || [];
  const ownerPct = (inv.owner_percent || 100) / 100;

  const hasDirectAllocations = entries.some(
    (e) => !e.sale_opportunity_id && e.is_direct_allocation
  );

  const isPayout = !!(inv.add_sale_proceeds) || !!(inv.is_commission_split_invoice) ||
    entries.some((e) => e.is_proceeds);

  if (!hasDirectAllocations) {
    // ── Original path ───────────────────────────────────────────────────────
    const expenseSubtotal = entries
      .filter((e) => !e.sale_opportunity_id && !e.is_proceeds)
      .reduce((s, e) => s + (e.total !== undefined ? e.total : (e.price || 0) * (e.units || 1)), 0);
    const discountTotal = inv.discount_total || 0;
    const expenseTotal  = expenseSubtotal - discountTotal;

    if (isPayout && inv.add_sale_proceeds) {
      const tenantCommission = inv.commission_amount || 0;
      return Math.max(0, (expenseTotal + tenantCommission) * ownerPct);
    }
    return Math.max(0, expenseTotal * ownerPct);
  }

  // ── Direct Allocation path ────────────────────────────────────────────────
  const sharedExpenses = entries
    .filter((e) => !e.sale_opportunity_id && !e.is_proceeds && !e.is_direct_allocation)
    .reduce((s, e) => s + (e.total !== undefined ? e.total : (e.price || 0) * (e.units || 1)), 0);
  const directExpenses = entries
    .filter((e) => !e.sale_opportunity_id && !e.is_proceeds && !!e.is_direct_allocation)
    .reduce((s, e) => s + (e.total !== undefined ? e.total : (e.price || 0) * (e.units || 1)), 0);

  const totalExpenses    = sharedExpenses + directExpenses;
  const discountTotal    = inv.discount_total || 0;
  const discountOnShared = totalExpenses > 0 ? discountTotal * (sharedExpenses / totalExpenses) : 0;
  const discountOnDirect = totalExpenses > 0 ? discountTotal * (directExpenses / totalExpenses) : discountTotal;

  const sharedNet = sharedExpenses - discountOnShared;
  const directNet = directExpenses - discountOnDirect;

  if (isPayout && inv.add_sale_proceeds) {
    const tenantCommission = inv.commission_amount || 0;
    return Math.max(0, (sharedNet + tenantCommission) * ownerPct + directNet);
  }
  return Math.max(0, sharedNet * ownerPct + directNet);
}
